import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Close } from '@videoblocks/react-icons';

import { getPreviewUrl } from '../../../../../common/utils';
import { closeVideoEditorModal } from '../../../actions/SearchActions';
import {
  selectVideoEditorModalIsOpen,
  selectVideoEditorModalStockItem,
  selectShouldShowAIEditFeatures,
} from '../../../selectors/searchSelectors';
import VideoEditResult from './VideoEditResult';
import VideoEditorUI from './VideoEditorUI';
import LoadingState from './components/LoadingState';
import { useCases } from './utils/constants';
import { VideoMetadata, VideoSelection } from './utils/videoUtils';

const VideoEditorModal = () => {
  const dispatch = useDispatch();
  const isOpen = useSelector(selectVideoEditorModalIsOpen);
  const selectedStockItem = useSelector(selectVideoEditorModalStockItem);
  const shouldShowAIEditFeatures = useSelector(selectShouldShowAIEditFeatures);
  const [isPlaying, setIsPlaying] = useState(false);
  const [selectedUseCase, setSelectedUseCase] = useState<string | null>(
    useCases[0].id
  );
  const [prompt, setPrompt] = useState(useCases[0].defaultText);
  const [isEditing, setIsEditing] = useState(true);

  const [isLoadingMetadata, setIsLoadingMetadata] = useState(true);
  const [videoMetadata, setVideoMetadata] = useState<VideoMetadata | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);

  // Get current suggestions based on selected use case
  const currentSuggestions = useMemo(() => {
    if (!selectedUseCase) return [];
    const useCase = useCases.find((uc) => uc.id === selectedUseCase);
    return useCase ? useCase.suggestions : [];
  }, [selectedUseCase]);

  const videoSrc = useMemo(() => {
    if (!selectedStockItem) return null;

    // Use the preview URL if available
    if (selectedStockItem.previewUrl) {
      return selectedStockItem.previewUrl;
    }

    return getPreviewUrl(selectedStockItem.id, selectedStockItem.contentClass);
  }, [selectedStockItem]);

  // Load video metadata immediately when modal opens
  useEffect(() => {
    if (!isOpen || !videoSrc) {
      setVideoMetadata(null);
      setIsLoadingMetadata(true);
      return;
    }

    const loadMetadata = async () => {
      try {
        setIsLoadingMetadata(true);
        setError(null);

        const video = document.createElement('video');
        video.src = videoSrc;
        video.preload = 'metadata';
        video.muted = true;

        const metadata = await new Promise<VideoMetadata>((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error('Video metadata load timeout'));
          }, 5000);

          video.addEventListener('loadedmetadata', () => {
            clearTimeout(timeout);
            resolve({
              duration: video.duration,
              width: video.videoWidth,
              height: video.videoHeight,
            });
          });

          video.addEventListener('error', () => {
            clearTimeout(timeout);
            reject(new Error('Video failed to load'));
          });
        });

        setVideoMetadata(metadata);
        setIsLoadingMetadata(false);
      } catch (error) {
        console.error('❌ Video metadata loading failed:', error);
        setError(
          error instanceof Error ? error.message : 'Failed to load video'
        );
        setIsLoadingMetadata(false);
      }
    };

    loadMetadata();
  }, [isOpen, videoSrc]);

  const handlePlayPause = () => setIsPlaying(!isPlaying);

  const handleUseCaseSelect = (id: string) => {
    setSelectedUseCase(id);

    const useCase = useCases.find((uc) => uc.id === id);
    if (useCase) {
      setPrompt(useCase.defaultText);
    }
  };

  const handleGenerateVideo = (selection?: VideoSelection) => {
    alert(
      `Generating video with selection: ${
        selection
          ? `${selection.startTime}s - ${selection.endTime}s`
          : 'full video'
      }`
    );
  };

  const handleSuggestionClick = (suggestion: string) => {
    setPrompt(suggestion);
  };

  const handleClose = () => {
    setIsPlaying(false);
    setSelectedUseCase(null);
    setPrompt('');
    dispatch(closeVideoEditorModal());
  };

  if (!isOpen || !shouldShowAIEditFeatures) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-1000 text-white">
      <button
        className="absolute top-0 right-0 mr-9 mt-9 z-1000"
        onClick={handleClose}
      >
        <Close className="fill-current h-6 w-6 text-white" />
      </button>

      {/* Show UI immediately once metadata is loaded */}
      <div
        className="flex flex-col items-center px-24"
        style={{
          backgroundColor: '#1A1C1E99',
          opacity: 60,
          backdropFilter: 'blur(80px)',
          width: '1078px',
          height: '100%',
        }}
      >
        {isLoadingMetadata || error ? (
          <LoadingState
            progress={50}
            message={error ? `Error: ${error}` : 'Loading video...'}
          />
        ) : isEditing ? (
          <VideoEditorUI
            src={videoSrc}
            videoMetadata={videoMetadata}
            isPlaying={isPlaying}
            selectedUseCase={selectedUseCase}
            prompt={prompt}
            useCases={useCases}
            suggestions={currentSuggestions}
            onPlayPause={handlePlayPause}
            onUseCaseSelect={handleUseCaseSelect}
            onPromptChange={(e) => setPrompt(e.target.value)}
            onGenerateVideo={handleGenerateVideo}
            onSuggestionClick={handleSuggestionClick}
          />
        ) : (
          <VideoEditResult />
        )}
      </div>
    </div>
  );
};

export default VideoEditorModal;
